// Retain the existing element name so cached cart markup also uses quantity-only validation.
export function isCartCheckoutReady({ cartEmpty = false, trayInvalid = false } = {}) {
  return !cartEmpty && !trayInvalid;
}

export function calculateTrayState({ totalTrays, traysPerOrder = 6, bottlesPerTray = 8 }) {
  if (totalTrays <= 0) return { invalid: false, message: '' };

  if (totalTrays < traysPerOrder) {
    return {
      invalid: true,
      message: `Noch ${(traysPerOrder - totalTrays) * bottlesPerTray} Flaschen bis zur Mindestbestellmenge.`,
    };
  }

  const remainder = totalTrays % traysPerOrder;
  if (remainder !== 0) {
    return {
      invalid: true,
      message: `Noch ${(traysPerOrder - remainder) * bottlesPerTray} Flaschen hinzufügen, um die Bestellmenge abzuschließen.`,
    };
  }

  return { invalid: false, message: '' };
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class CartQuantityGate extends HTMLElementBase {
  static observedAttributes = ['data-tray-invalid', 'data-cart-empty', 'data-tray-message'];

  connectedCallback() {
    const scope = this.closest('.cart-drawer') || this.closest('.cart-summary') || document;
    this.checkoutButtons = [...scope.querySelectorAll('button[name="checkout"]')];
    this.footerStatuses = [...scope.querySelectorAll('[data-b2b-footer-status]')];
    this.cartScope = this.closest('cart-items-component') || this.closest('.cart-drawer') || this.closest('.cart-summary');
    this.optimisticTrayState = null;
    this.quantityPending = false;
    this.onQuantityUpdate = (event) => {
      if (!this.cartScope || !(event.target instanceof Node) || !this.cartScope.contains(event.target)) return;
      this.quantityPending = event.type === 'quantity-selector:update';
      this.optimisticTrayState = this.calculateOptimisticTrayState();
      this.updateUI();
    };
    this.onCartUpdate = () => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.optimisticTrayState = null;
        this.quantityPending = false;
        this.updateUI();
      });
    };
    this.onSyncState = () => this.updateUI();
    document.addEventListener('cart:sync-state', this.onSyncState);
    document.addEventListener('quantity-selector:update', this.onQuantityUpdate);
    document.addEventListener('cart:quantity-reconciled', this.onQuantityUpdate);
    document.addEventListener('cart:update', this.onCartUpdate);
    this.updateUI();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:sync-state', this.onSyncState);
    document.removeEventListener('quantity-selector:update', this.onQuantityUpdate);
    document.removeEventListener('cart:quantity-reconciled', this.onQuantityUpdate);
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.updateUI();
  }

  calculateOptimisticTrayState() {
    const rows = this.cartScope?.querySelectorAll('.cart-drawer-product[data-tray-item="true"]');
    if (!rows?.length) return null;

    let totalTrays = 0;
    rows.forEach((row) => {
      const input = row.querySelector('cart-quantity-selector-component input[ref="quantityInput"]');
      totalTrays += Number(input?.value) || 0;
    });

    return calculateTrayState({
      totalTrays,
      traysPerOrder: Number(this.dataset.traysPerOrder) || 6,
      bottlesPerTray: Number(this.dataset.bottlesPerTray) || 8,
    });
  }

  isComplete() {
    if (this.quantityPending || window.cartSyncState?.pending || window.cartSyncState?.failed) return false;
    return isCartCheckoutReady({
      cartEmpty: this.dataset.cartEmpty === 'true',
      trayInvalid: this.optimisticTrayState?.invalid ?? (this.dataset.trayInvalid === 'true'),
    });
  }

  updateUI() {
    const complete = this.isComplete();
    this.checkoutButtons?.forEach((button) => {
      button.disabled = !complete;
      button.setAttribute('aria-disabled', String(!complete));
    });
    this.footerStatuses?.forEach((status) => {
      status.textContent = window.cartSyncState?.failed ? 'Warenkorb konnte nicht aktualisiert werden. Bitte lade die Seite neu.'
        : this.quantityPending || window.cartSyncState?.pending ? 'Warenkorb wird aktualisiert …'
        : complete ? '' : this.dataset.cartEmpty === 'true'
        ? 'Dein Warenkorb ist leer.'
        : this.optimisticTrayState?.message || this.dataset.trayMessage || 'Bestellmenge anpassen.';
      status.hidden = complete;
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('b2b-cart-gate')) {
  customElements.define('b2b-cart-gate', CartQuantityGate);
}

if (typeof document !== 'undefined') {
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-form')) return;
    const scope = form.closest('cart-items-component') || form.closest('.cart-drawer') || form.closest('.cart-summary');
    if (!scope) return;
    if ([...scope.querySelectorAll('b2b-cart-gate')].some((gate) => !gate.isComplete())) {
      event.preventDefault();
    }
  }, true);
}
